import React, { useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import StudyResourceService from "../../../_services/StudyResourceService";
import WorkflowBuilder from "./WorkflowBuilder";
import { ClinicalSpec, ClinicalSourceRecord, getClinicalSpec, CLIENT_CONFIGURATION_REQUIRED } from "./clinical-spec-types";
import { validateWorkflow } from "./workflow-types";
import { ClinicalMasters } from "../../../_services/ClinicalMastersService";
import {
  ListEditor,
  ClassControl,
  ClassBadge,
  SpecRef,
  PathwaySpecSections,
  MedicationMatrixSections,
  GuardrailSpecSections,
  AuthoritySpecSections,
  MonitoringSpecSections,
  AiWorkspaceSpecSections,
  SourcesSpecSections,
  FormularySpecSections,
  TestingValidationSections,
  GovernanceSections,
  GovernanceRecord,
  DeploymentLock,
  DecisionSequenceMap,
} from "./ClinicalSpecSections";
import {
  WorkflowV2,
  WorkflowState,
  WorkflowTransition,
  WorkflowGuard,
  FieldGuard,
  WorkflowCaptureField,
  isExecutableWorkflow,
  CaptureType,
  FieldOp,
} from "./workflow-types";

/**
 * Decision Resource Studio — the authoring environment for Interactive
 * Pathway (type 40) resources. The portlet menu mirrors DeciiQ slide 4's
 * "Build and govern the Decision Resource" list exactly — ten items in slide
 * order: the five clinical specifications (§8–§12), then Approved Clinical
 * Sources (§5), Client Formulary & Preferences (§7/§9.3), AI Workspace
 * Configuration (§13.2–13.4), Testing & Validation (§13.5/§18.2), and
 * Versioning & Approvals (§13.1/§13.5). The former Setup portlet is dissolved
 * into the last item (identity, lineage, gate) with its §5 table moved to
 * Approved Clinical Sources and its human gate checkboxes to Testing &
 * Validation.
 *
 * The matrix portlets (guardrails / authority / monitoring) are PROJECTIONS
 * over the single workflow v2 definition — they read and write the same
 * `config.workflow` object the WorkflowBuilder edits, never a parallel copy,
 * so the artifact views and what the server enforces cannot drift.
 */

interface Props {
  content: string;
  onContentChange: (content: string) => void;
  /** New resources get the quick-start template gallery. */
  isNew: boolean;
  /** When the resource has no name yet, applying a template suggests one. */
  hasName: boolean;
  onSuggestName: (name: string) => void;
  /** Open on a specific portlet (deep link from the launcher artifact cards). */
  initialPortlet?: string;
  /** Clinical Source master registry — feeds §5/§13.3 pickers (derived from masters when absent). */
  sourceRegistry?: ClinicalSourceRecord[];
  /** Full clinical masters (parameters, medication classes, roles…) — feeds all vocabulary pickers. */
  clinicalMasters?: ClinicalMasters | null;
  /** Whether the resource has a grading rubric attached (resource-level field) — feeds the §13.5 gate. */
  hasRubric?: boolean;
  /**
   * Show the legacy non-clinical demo playbook gallery for new resources
   * (Payer/Provider/HSA-FSA…). The DRS studio page disables it — clinical
   * resources start from SynerGen references or blank, through governance.
   */
  showDemoTemplates?: boolean;
}

/** One computed §13.5 gate item: derived from the decision resource + masters state. */
export interface GateComputation {
  status: "met" | "failed" | "na";
  detail: string;
}

export type PortletId =
  | "pathways" | "matrix" | "guardrails" | "authority" | "monitoring"
  | "sources" | "formulary" | "ai" | "testing" | "versioning";

/** The DeciiQ slide-4 "Build and govern the Decision Resource" list, verbatim and in slide order. */
export const STUDIO_PORTLETS: Array<{ id: PortletId; icon: string; label: string; hint: string }> = [
  { id: "pathways", icon: "🧭", label: "Clinical Pathway Specification", hint: "Primary vs secondary; 4 operational pathways; decision sequence (§8)" },
  { id: "matrix", icon: "💊", label: "Medication Decision Matrix", hint: "Classes, rules, eligibility, preferences (§9)" },
  { id: "guardrails", icon: "🛡️", label: "Safety & Guardrail Matrix", hint: "Hard stops, escalation, human review rules (§10)" },
  { id: "authority", icon: "👥", label: "Authority & Escalation Matrix", hint: "Pharmacist scope, approvals, handoffs (§11)" },
  { id: "monitoring", icon: "📈", label: "Monitoring & Outcome Specification", hint: "Follow-up, labs, targets, adherence (§12)" },
  { id: "sources", icon: "📚", label: "Approved Clinical Sources", hint: "Guidelines, references, local protocols (§5)" },
  { id: "formulary", icon: "🧾", label: "Client Formulary & Preferences", hint: "Preferred agents, restrictions, tiers (§7 / §9.3)" },
  { id: "ai", icon: "✨", label: "AI Workspace Configuration", hint: "RAG sources, prompts, templates, rubrics, model settings (§13.2–13.4)" },
  { id: "testing", icon: "🧪", label: "Testing & Validation", hint: "Prompt test, safety test, SME review (§13.5 / §18.2)" },
  { id: "versioning", icon: "✅", label: "Versioning & Approvals", hint: "Identity, lineage, publication gate — audit-ready governance (§13.1 / §13.5)" },
];

/** Old portlet ids still reachable via saved deep links (?portlet=setup). */
const LEGACY_PORTLET_ALIASES: Record<string, PortletId> = { setup: "versioning" };

/** Resolve a raw ?portlet= value (current id or legacy alias) to a portlet id. */
export const resolvePortletId = (raw: string | null | undefined): PortletId | null => {
  if (!raw) return null;
  const aliased = LEGACY_PORTLET_ALIASES[raw] ?? raw;
  return STUDIO_PORTLETS.find((p) => p.id === aliased)?.id ?? null;
};

const FIELD_OPS: FieldOp[] = ["notEmpty", "equals", "notEquals", "gt", "gte", "lt", "lte", "in"];
const CAPTURE_TYPE_OPTIONS: CaptureType[] = ["text", "textarea", "number", "select", "boolean"];
const PATHWAY_COLORS = ["#e53e3e", "#38a169", "#d69e2e", "#805ad5"];

const KIND_BADGE: Record<string, { icon: string; label: string }> = {
  advance: { icon: "→", label: "advance" },
  hold: { icon: "⏸", label: "hold" },
  escalate: { icon: "⚠", label: "escalate" },
  return: { icon: "↩", label: "return" },
};

/** Human sentence for a guard — the same semantics the server enforces. */
const guardSentence = (guard: WorkflowGuard | undefined): string => {
  if (!guard) return "No rule — always allowed";
  if (guard.type === "manual") {
    return guard.roles && guard.roles.length > 0
      ? `Only ${guard.roles.join(" / ")} may perform this`
      : "Authorized click (anyone)";
  }
  if (guard.type === "aiReview") return "Requires explicit human-review acknowledgment";
  const val = Array.isArray(guard.value) ? guard.value.join(", ") : String(guard.value ?? "");
  const ops: Record<FieldOp, string> = {
    notEmpty: "must be filled in",
    equals: `must equal ${val}`,
    notEquals: `must not equal ${val}`,
    gt: `must be > ${val}`,
    gte: `must be ≥ ${val}`,
    lt: `must be < ${val}`,
    lte: `must be ≤ ${val}`,
    in: `must be one of ${val}`,
  };
  return `"${guard.field}" ${ops[guard.op] ?? guard.op}`;
};

const DecisionResourceStudio: React.FC<Props> = ({ content, onContentChange, isNew, hasName, onSuggestName, initialPortlet, sourceRegistry, clinicalMasters, hasRubric, showDemoTemplates = true }) => {
  const [activePortlet, setActivePortlet] = useState<PortletId>(() => {
    const match = resolvePortletId(initialPortlet);
    return match ?? (isNew ? "versioning" : "pathways");
  });
  const [seqOverlayOpen, setSeqOverlayOpen] = useState(false);
  const [activePathwayIdx, setActivePathwayIdx] = useState(0);
  const [suggestingGuardrails, setSuggestingGuardrails] = useState(false);
  const [suggestGuardrailsError, setSuggestGuardrailsError] = useState<string | null>(null);
  const [newRole, setNewRole] = useState("");
  const [extraRoles, setExtraRoles] = useState<string[]>([]);

  let config: any = {};
  try { config = JSON.parse(content || "{}"); } catch { config = {}; }
  const pathways: any[] = config.pathways || [];
  const attrs: any[] = config.attributes || [];

  const updateConfig = (updates: any) => {
    const next = { ...config, ...updates };
    // §4 publication boundary: any edit to a published resource reverts it to
    // draft — the last published snapshot stays effective for learners until
    // the author republishes.
    if (config.publication?.status === "published" && !("publication" in updates)) {
      next.publication = { ...config.publication, status: "draft" };
    }
    onContentChange(JSON.stringify(next, null, 2));
  };

  // ── Workflow projection helpers — the single source of truth is config.workflow ──
  const def: WorkflowV2 | null = isExecutableWorkflow(config.workflow) ? config.workflow : null;

  const updateWorkflow = (patch: Partial<WorkflowV2>) => {
    if (def) updateConfig({ workflow: { ...def, ...patch } });
  };
  const updateTransition = (index: number, patch: Partial<WorkflowTransition>) => {
    if (def) updateWorkflow({ transitions: def.transitions.map((t, i) => (i === index ? { ...t, ...patch } : t)) });
  };
  const updateWfState = (index: number, patch: Partial<WorkflowState>) => {
    if (def) updateWorkflow({ states: def.states.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  };
  const stateLabel = (id: string): string => def?.states.find((s) => s.id === id)?.label ?? id;
  const allCaptureFields: WorkflowCaptureField[] = def ? def.states.flatMap((s) => s.capture ?? []) : [];

  // ── Clinical specification (config.clinicalSpec — §5–§13 governed content) ──
  const spec: ClinicalSpec = getClinicalSpec(config);
  const updateSpec = (patch: Partial<ClinicalSpec>) => updateConfig({ clinicalSpec: { ...spec, ...patch } });

  // ── Master vocabularies feeding the pickers ──
  const masterParameters = clinicalMasters?.parameters;
  const masterMedClasses = clinicalMasters?.medicationClasses;
  const masterRoles = clinicalMasters?.shared?.roles ?? [];

  // Derived client copy: class is provenance (A locked, B inherited until
  // touched, touched/added content auto-classes C). References and standalone
  // authoring keep full class assignment.
  const derived = !!config.basedOn && !config.isBaseTemplate;

  const setTransitionGuard = (index: number, guardType: string) => {
    let guard: WorkflowGuard | undefined;
    if (guardType === "manual") guard = { type: "manual" };
    else if (guardType === "aiReview") guard = { type: "aiReview" };
    else if (guardType === "field") guard = { type: "field", field: allCaptureFields[0]?.id ?? "", op: "notEmpty" };
    else guard = undefined;
    updateTransition(index, { guard });
  };

  // ── Governance profile — drives tab badges and the guardrails summary ──
  const transitions = def?.transitions ?? [];
  const hardStops = transitions.filter((t) => t.kind === "advance" && t.guard?.type === "field");
  const checkpoints = transitions.filter((t) => t.guard?.type === "aiReview");
  const safetyExits = transitions.filter((t) => t.kind !== "advance");
  const restricted = transitions.filter((t) => t.guard?.type === "manual" && (t.guard.roles ?? []).length > 0);
  const knownRoles = Array.from(
    new Set([
      ...masterRoles,
      ...transitions.flatMap((t) => (t.guard?.type === "manual" ? t.guard.roles ?? [] : [])),
      ...extraRoles,
    ]),
  );
  const totalCaptureFields = allCaptureFields.length;

  // Each badge counts the portlet's PRIMARY governed list, so every number
  // traces to one section of the requirements document (not workflow machinery).
  const portletBadge = (id: PortletId): number | null => {
    switch (id) {
      case "pathways": return pathways.length || (spec.operationalPathways?.length ?? 0) || null; // §8.2 operational pathways
      case "matrix": return spec.medicationMatrix?.rows?.length || null; // §9.2 matrix rows
      case "guardrails": return spec.guardrails?.rules?.length || null; // §10.2 reference guardrails
      case "authority": return spec.authority?.matrix?.length || null; // §11.2 escalation matrix rows
      case "monitoring": return spec.monitoring?.baselineEvidence?.length || null; // §12.1 baseline evidence elements
      case "sources": return spec.clinicalSources?.length || null; // §5 hierarchy rows
      case "formulary": return spec.clientFormulary?.preferredAgents?.length || null; // §9.3 preferred agents
      case "ai": return spec.aiWorkspace?.promptSet?.length || null; // §13.4 prompt set
      case "testing": return (spec.publicationGate?.items || []).filter((it) => !computedGate[it.id]).length || null; // §13.5 human attestations
      default: return null;
    }
  };

  // ── §13.5 computed publication gate (layer-aware) ──
  const wfErrors = def ? validateWorkflow(def) : [];
  const computedGate: Record<string, GateComputation> = (() => {
    const out: Record<string, GateComputation> = {};
    const isBase = !!config.isBaseTemplate;

    const cond = clinicalMasters?.conditions.find((c) => c.conditionKey === config.condition);
    if (cond && clinicalMasters) {
      const refSources = clinicalMasters.sources.filter((s) => cond.sourceKeys.includes(s.sourceKey));
      const unapproved = refSources.filter((s) => s.approvalStatus !== "approved");
      out.pg_sources = unapproved.length === 0
        ? { status: "met", detail: `all ${refSources.length} sources referenced by "${cond.name}" are approved` }
        : { status: "failed", detail: `unapproved: ${unapproved.map((s) => s.name).join("; ")}` };
      const notRag = refSources.filter((s) => s.ragStatus !== "ragified" && s.ragStatus !== "not-applicable");
      out.pg_rag = notRag.length === 0
        ? { status: "met", detail: "every applicable source is RAGified" }
        : { status: "failed", detail: `RAG pending: ${notRag.map((s) => s.name).join("; ")}` };
    }

    const artifacts: Array<[string, boolean]> = [
      ["Clinical Pathway Specification", (spec.classification?.categories?.length ?? 0) > 0 && (config.pathways || []).some((p: any) => (p.referenceConditions || []).length > 0)],
      ["Medication Decision Matrix", (spec.medicationMatrix?.rows?.length ?? 0) > 0],
      ["Safety & Guardrail Matrix", (spec.guardrails?.rules?.length ?? 0) > 0],
      ["Authority & Escalation Matrix", (spec.authority?.matrix?.length ?? 0) > 0],
      ["Monitoring & Outcome Specification", (spec.monitoring?.baselineEvidence?.length ?? 0) > 0],
    ];
    const missingArtifacts = artifacts.filter(([, ok]) => !ok).map(([n]) => n);
    out.pg_rules = missingArtifacts.length === 0
      ? { status: "met", detail: "all five specification artifacts are populated" }
      : { status: "failed", detail: `empty: ${missingArtifacts.join(", ")}` };

    if (def) {
      const missingExit = def.states.filter(
        (s) => (s.kind === "task" || s.kind === "decision") && !def.transitions.some((t) => t.from === s.id && t.kind !== "advance"),
      ).length;
      const problems = [
        wfErrors.length ? `${wfErrors.length} workflow issue(s)` : "",
        missingExit ? `${missingExit} state(s) without a safety exit` : "",
        (spec.guardrails?.rules?.length ?? 0) === 0 ? "no guardrails configured" : "",
      ].filter(Boolean);
      out.pg_guardrails = problems.length === 0
        ? { status: "met", detail: "machine valid, safety exits present, guardrails configured" }
        : { status: "failed", detail: problems.join("; ") };
      out.pg_escalation = def.transitions.some((t) => t.kind === "escalate")
        ? { status: "met", detail: "escalation routes present in the executable machine" }
        : { status: "failed", detail: "no escalate transitions in the machine" };
    } else {
      out.pg_guardrails = { status: "failed", detail: "workflow is display-only — upgrade to a state machine" };
      out.pg_escalation = { status: "failed", detail: "workflow is display-only — upgrade to a state machine" };
    }

    // §10.2 + §6: no Class A safety claim may ship unaccounted — each must be
    // implemented by declared machinery (transition/rule implementsGuardrail)
    // or carry an explicit deferral naming where fulfillment happens.
    {
      const gRules = spec.guardrails?.rules ?? [];
      const isLinked = (id?: string) =>
        !!id && (
          (def?.transitions ?? []).some((t) => t.implementsGuardrail === id) ||
          (spec.medicationMatrix?.rows ?? []).some((row) => (row.rules ?? []).some((er) => er.implementsGuardrail === id))
        );
      const nakedA = gRules.filter((r) => r.ruleClass === "A" && !isLinked(r.id) && !r.deferral);
      if (gRules.length > 0) {
        out.pg_class_a_guardrails = nakedA.length === 0
          ? { status: "met", detail: "every Class A guardrail is machinery-linked or explicitly deferred" }
          : { status: "failed", detail: `unaccounted Class A: ${nakedA.map((r) => r.trigger || r.id).join("; ")}` };
      }
    }

    out.pg_authority = (spec.authority?.matrix?.length ?? 0) > 0 && (spec.authority?.scopes?.length ?? 0) > 0
      ? { status: "met", detail: "authority matrix and scopes configured" }
      : { status: "failed", detail: "authority matrix or scopes are empty" };

    const ccrCount = [
      ...(spec.authority?.matrix ?? []).map((r) => r.deploymentValue ?? CLIENT_CONFIGURATION_REQUIRED),
      ...(spec.authority?.scopes ?? []).map((s) => s.value),
      ...(spec.clientConfiguration?.unresolved ?? []).map((u) => u.value),
    ].filter((v) => v === CLIENT_CONFIGURATION_REQUIRED).length;
    out.pg_client_fields = isBase
      ? { status: "na", detail: "base template — deployment values stay intentionally unresolved (§7); resolved on client copies" }
      : ccrCount === 0
        ? { status: "met", detail: "no CLIENT_CONFIGURATION_REQUIRED values remain" }
        : { status: "failed", detail: `${ccrCount} unresolved deployment value(s) — §20.3 blocks production` };

    out.pg_rubric = hasRubric
      ? { status: "met", detail: "grading rubric attached to the resource" }
      : { status: "failed", detail: "no rubric attached (resource-level Grading Rubric field)" };
    out.pg_workspace = spec.aiWorkspace?.workspace
      ? { status: "met", detail: spec.aiWorkspace.workspace.name }
      : { status: "failed", detail: "no AI workspace configuration" };
    out.pg_version = isBase
      ? (config.templateVersion
        ? { status: "met", detail: `SynerGen reference v${config.templateVersion}` }
        : { status: "failed", detail: "base template without a version" })
      : (config.basedOn?.version
        ? { status: "met", detail: `derived from ${config.basedOn.templateKey} v${config.basedOn.version}` }
        : { status: "failed", detail: "no version lineage (not derived from a base)" });

    return out;
  })();

  // Promotion to base requires the computable core to pass.
  const promoteBlockers = ["pg_rules", "pg_guardrails"]
    .filter((id) => computedGate[id]?.status !== "met")
    .map((id) => computedGate[id]?.detail ?? id);

  // ── Shared: portlets that require an executable machine ──
  const renderNeedsStateMachine = (what: string) => (
    <div className="drs-upgrade-panel">
      <div className="drs-upgrade-title">This portlet governs the executable workflow</div>
      <p>
        {what} are enforced by the workflow state machine. This resource still uses a display-only
        workflow list — upgrade it to a state machine first, then author the rules here.
      </p>
      <Button type="button" size="sm" variant="primary" onClick={() => setActivePortlet("pathways")}>
        Open Pathway Specification →
      </Button>
    </div>
  );

  // ────────────────── Versioning & Approvals portlet ──────────────────
  // (absorbs the former Setup: resource basics, identity & lineage, profile,
  // publication gate, CCR ledger — the slide's "audit-ready governance")
  const sampleData = [
    { category: 'Healthcare & Life Sciences', samples: [
      { label: 'Payer Playbook', data: { showJustification: false, enableAIScoring: false, title: 'Payer Playbook — Cost vs Outcome Optimization', description: 'Evaluate the treatment pathways below and make a decision based on cost and outcome tradeoffs.', labels: { approach: 'Clinical Approach', sequence: 'Sequence of care', workflow: 'Clinical Workflow' }, workflow: ['Patient', 'Diagnosis', 'Treatment', 'Utilization', 'Outcome', 'Cost'], attributes: [{ name: 'Cost', unit: '$', direction: 'lower-is-better' }, { name: 'Outcome', unit: '', direction: 'higher-is-better' }, { name: 'Readmission Risk', unit: '%', direction: 'lower-is-better' }], pathways: [
        { id: 'A', label: 'Pathway A', title: 'Conservative Management', indications: 'Best for: low-acuity, stable patients with good access to primary care', rationale: 'Front-loads the lowest-cost interventions and relies on generic medications plus telehealth. Works when the underlying condition is stable and rapid escalation isn\'t required, but accepts a higher readmission profile in exchange for lower upfront spend.', steps: ['Primary Care', 'Generic Rx', 'Telehealth Follow-up'], description: 'Lower upfront cost using generic medications and telemedicine.', values: { 'Cost': 12000, 'Outcome': 'Medium', 'Readmission Risk': 18 } },
        { id: 'B', label: 'Pathway B', title: 'Balanced Protocol', indications: 'Best for: moderate-risk patients where outcome quality matters but cost is still a constraint', rationale: 'Brings in specialist judgment and targeted therapy early enough to meaningfully shift outcomes without committing to the most expensive interventions. The sweet spot for most population-health use cases.', steps: ['Specialist Consult', 'Targeted Therapy', 'In-Person Monitoring'], description: 'Balanced approach with specialist involvement and targeted treatment.', values: { 'Cost': 28000, 'Outcome': 'High', 'Readmission Risk': 8 } },
        { id: 'C', label: 'Pathway C', title: 'Aggressive Intervention', indications: 'Best for: high-acuity or complex cases where clinical outcome trumps cost', rationale: 'Pulls every lever — multi-specialist team, advanced biologics, continuous monitoring, structured rehab. Cost is highest by far but readmission risk is minimized and outcomes are maximized. Use when the alternative is a catastrophic clinical event.', steps: ['Multi-Specialist Team', 'Advanced Biologics', 'Continuous Monitoring', 'Rehab Program'], description: 'Highest cost but best clinical outcomes.', values: { 'Cost': 65000, 'Outcome': 'Best', 'Readmission Risk': 3 } }
      ] } },
      { label: 'Provider Playbook', data: { showJustification: false, enableAIScoring: false, title: 'Provider Playbook — Care Pathway Optimization', description: 'Optimize the care pathway balancing length of stay against readmission risk.', labels: { approach: 'Clinical Approach', sequence: 'Sequence of care', workflow: 'Clinical Workflow' }, workflow: ['Patient', 'Diagnosis', 'Treatment', 'Length of Stay', 'Outcome'], attributes: [{ name: 'Cost', unit: '$', direction: 'lower-is-better' }, { name: 'Outcome', unit: '', direction: 'higher-is-better' }, { name: 'Readmission Risk', unit: '%', direction: 'lower-is-better' }], pathways: [
        { id: 'A', label: 'Pathway A', title: 'Early Discharge Protocol', indications: 'Best for: stable patients with reliable home support and clear discharge criteria', rationale: 'Trades inpatient bed-days for home monitoring. Frees up hospital capacity and reduces direct cost, but only works when the patient\'s social support system can absorb the transition — otherwise the readmission penalty wipes out the savings.', steps: ['Rapid Assessment', 'Accelerated Treatment', 'Early Discharge', 'Home Monitoring'], description: 'Shorter length of stay, higher readmission risk.', values: { 'Cost': 8500, 'Outcome': 'Moderate', 'Readmission Risk': 22 } },
        { id: 'B', label: 'Pathway B', title: 'Standard Care Protocol', indications: 'Best for: complex cases or unreliable post-discharge environments', rationale: 'Stays the conservative course with a full workup and phased step-down care. Costs more upfront in bed-days but the structured handoff protects against the readmission and complication risk that early-discharge protocols can\'t mitigate.', steps: ['Full Workup', 'Phased Treatment', 'Step-Down Care', 'Discharge Planning'], description: 'Longer LOS with structured step-down care.', values: { 'Cost': 18000, 'Outcome': 'High', 'Readmission Risk': 6 } }
      ] } },
      { label: 'Life Sciences', data: { showJustification: true, enableAIScoring: true, title: 'Life Sciences Playbook — Treatment Strategy Optimization', description: 'Choose a therapy strategy balancing response rate against scalability.', labels: { approach: 'Clinical Approach', sequence: 'Sequence of care', workflow: 'Clinical Workflow' }, workflow: ['Patient', 'Therapy', 'Response', 'Progression', 'Outcome'], attributes: [{ name: 'Cost', unit: '$', direction: 'lower-is-better' }, { name: 'Response Rate', unit: '%', direction: 'higher-is-better' }, { name: 'Outcome', unit: '', direction: 'higher-is-better' }], pathways: [
        { id: 'A', label: 'Pathway A', title: 'Precision Therapy', indications: 'Best for: genomically complex tumors, treatment-naive patients, well-characterized biomarkers', rationale: 'Uses genomic profiling to match patients to targeted agents only when the biology supports it. Higher per-patient cost but dramatically higher response rate in the matched population. Loses its edge when scaled across heterogeneous populations where biomarker matching becomes statistically noisy.', steps: ['Genomic Profiling', 'Biomarker Matching', 'Targeted Agent', 'Adaptive Monitoring'], description: 'Personalized treatment based on genomic profiling.', values: { 'Cost': 85000, 'Response Rate': 78, 'Outcome': 'Higher Response' } },
        { id: 'B', label: 'Pathway B', title: 'Standard Protocol', indications: 'Best for: population-scale rollouts, resource-constrained settings, established disease subtypes', rationale: 'A protocol designed to work for the largest number of patients with the simplest workflow. Sacrifices per-patient response quality for predictability, lower cost, and operational scalability — the right choice when the goal is reaching everyone, not optimizing for any individual.', steps: ['Standard Screening', 'First-Line Therapy', 'Periodic Review'], description: 'Standardized treatment protocol applicable at scale.', values: { 'Cost': 22000, 'Response Rate': 45, 'Outcome': 'Scalable' } }
      ] } },
    ] },
    { category: 'Finance', samples: [
      { label: 'HSA/FSA Member Playbook', data: { showJustification: true, enableAIScoring: true, title: 'HSA/FSA Member Playbook — Contribution Strategy Optimization', description: 'Help a member choose an HSA/FSA contribution strategy balancing tax savings, take-home cash flow, and healthcare affordability.', labels: { approach: 'Financial Approach', sequence: 'Member journey', workflow: 'Financial Workflow' }, workflow: ['Member Inquiry', 'Financial Assessment', 'Contribution Strategy', 'Benefit Utilization', 'Healthcare Spending', 'Financial Outcome'], attributes: [{ name: 'Tax Savings', unit: '$', direction: 'higher-is-better' }, { name: 'Take-home Cash Flow', unit: '$', direction: 'higher-is-better' }, { name: 'Healthcare Affordability', unit: '', direction: 'higher-is-better' }], pathways: [
        { id: 'A', label: 'Pathway A', title: 'Lower Contribution / Higher Cash Flow', indications: 'Best for: members with tight monthly budgets, low expected healthcare spend, limited emergency savings', rationale: 'Minimizes payroll deferral so monthly take-home stays high. Captures a small tax benefit while keeping liquidity available for non-healthcare needs. Trades long-term tax shelter and out-of-pocket coverage for short-term cash flow flexibility — appropriate when monthly affordability is the binding constraint.', steps: ['Annual contribution $1,500', 'Use FSA for known recurring costs', 'Pay larger OOP costs from cash'], description: 'Lower payroll deferral, higher take-home cash, limited healthcare affordability.', values: { 'Tax Savings': 360, 'Take-home Cash Flow': 6500, 'Healthcare Affordability': 'Limited' } },
        { id: 'B', label: 'Pathway B', title: 'Balanced Contribution / Balanced Wellness', indications: 'Best for: members with steady income and moderate expected healthcare spend; the default for most situations', rationale: 'Splits the dollar between current cash flow and tax-advantaged savings. Builds a meaningful HSA balance over time without starving the household budget. The sweet spot when neither liquidity nor long-term tax shelter is the dominant priority.', steps: ['Annual contribution $3,500', 'Cover routine OOP from HSA', 'Maintain emergency cash buffer'], description: 'Balanced payroll deferral, balanced financial wellness across cash flow and tax savings.', values: { 'Tax Savings': 840, 'Take-home Cash Flow': 4500, 'Healthcare Affordability': 'Good' } },
        { id: 'C', label: 'Pathway C', title: 'Maximum Contribution / Highest Tax Savings', indications: 'Best for: HDHP holders with stable cash flow, strong emergency reserves, long-term wealth-building intent', rationale: 'Pushes contribution to the IRS annual limit to capture maximum tax shelter and let the HSA compound as a stealth retirement account. Sacrifices monthly take-home in exchange for the strongest long-term financial outcome and full out-of-pocket coverage — only sustainable when monthly cash flow can absorb the deferral.', steps: ['Annual contribution $4,150 (IRS max)', 'Pay OOP from HSA debit', 'Invest excess HSA balance long-term'], description: 'Maximum payroll deferral, highest tax savings, strongest long-term outcome.', values: { 'Tax Savings': 996, 'Take-home Cash Flow': 3850, 'Healthcare Affordability': 'Excellent' } }
      ], evaluationCriteria: ['Quantifies the tax-savings vs cash-flow tradeoff with specific dollar figures', 'Addresses healthcare affordability and out-of-pocket coverage', 'Considers long-term compounding and retirement-style implications', 'References the member\'s liquidity / emergency reserves', 'Acknowledges IRS contribution limits and plan eligibility'] } },
      { label: 'Retirement Contribution Strategy', data: { showJustification: false, enableAIScoring: false, title: 'Retirement Contribution Strategy — Account Mix Optimization', description: 'Choose how to allocate retirement savings across taxable, tax-deferred, and tax-advantaged accounts.', labels: { approach: 'Financial Approach', sequence: 'Saver journey', workflow: 'Financial Workflow' }, workflow: ['Income Review', 'Goal Setting', 'Contribution Allocation', 'Account Selection', 'Investment Horizon', 'Retirement Outcome'], attributes: [{ name: 'Annual Tax Savings', unit: '$', direction: 'higher-is-better' }, { name: 'Projected Retirement Income', unit: '$/mo', direction: 'higher-is-better' }, { name: 'Liquidity', unit: '', direction: 'higher-is-better' }], pathways: [
        { id: 'A', label: 'Pathway A', title: 'Taxable Brokerage Only', indications: 'Best for: savers prioritizing flexibility, near-term goals, no employer match available', rationale: 'Keeps every dollar accessible at any age with no withdrawal penalties or distribution rules. Sacrifices tax-advantaged compounding for full liquidity — works when retirement is far off, employer match is absent, or short-term goals dominate.', steps: ['Open taxable brokerage', 'Auto-invest monthly', 'Rebalance annually'], description: 'Full liquidity, no tax shelter, simplest account structure.', values: { 'Annual Tax Savings': 0, 'Projected Retirement Income': 1800, 'Liquidity': 'High' } },
        { id: 'B', label: 'Pathway B', title: '401(k) Match + IRA', indications: 'Best for: most middle-income savers — captures the easy wins without complexity', rationale: 'Captures the full employer 401(k) match (free money) and adds an IRA for tax-advantaged compounding. The default starting point for retirement savings — high return on attention with manageable account complexity.', steps: ['Contribute to 401(k) up to match', 'Max IRA ($7,000)', 'Auto-invest both accounts'], description: 'Captures employer match plus IRA tax shelter; balanced liquidity.', values: { 'Annual Tax Savings': 2400, 'Projected Retirement Income': 3200, 'Liquidity': 'Moderate' } },
        { id: 'C', label: 'Pathway C', title: 'Max 401(k) + IRA + HSA Stack', indications: 'Best for: high earners with strong cash flow, long horizon, HDHP enrollment, retirement-focused intent', rationale: 'Stacks every tax-advantaged account at its IRS limit. Highest possible tax shelter and strongest projected retirement income, but locks dollars behind age and qualified-use restrictions. Only appropriate when emergency reserves are healthy and current cash flow can absorb the deferral.', steps: ['Max 401(k) ($23,000)', 'Max IRA ($7,000)', 'Max HSA ($4,150)', 'Invest HSA long-term'], description: 'Maximum tax shelter across all three account types; lowest liquidity.', values: { 'Annual Tax Savings': 8500, 'Projected Retirement Income': 5400, 'Liquidity': 'Low' } }
      ] } },
    ] },
  ];

  const renderVersioning = () => (
    <div>
      {isNew && !showDemoTemplates && (
        <div className="drs-hint" style={{ marginBottom: 12 }}>
          Starting fresh. For clinical work, prefer copying a SynerGen reference from the studio landing —
          it arrives governed (classes, sources, gate) with lineage recorded.
        </div>
      )}
      {isNew && showDemoTemplates && (
        <>
          <div className="drs-section-label">Quick-start templates <small>(non-clinical demo playbooks — no governance content)</small></div>
          {sampleData.map((group) => (
            <div key={group.category} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-accent)', marginBottom: 6, letterSpacing: '0.02em' }}>
                {group.category}
              </div>
              <div className="sr-template-grid">
                {group.samples.map((sample) => (
                  <button
                    key={sample.label}
                    type="button"
                    className="sr-template-card"
                    onClick={() => {
                      onContentChange(JSON.stringify(sample.data, null, 2));
                      if (!hasName) onSuggestName(sample.data.title);
                    }}
                  >
                    <div className="sr-template-name">{sample.label}</div>
                    <div className="sr-template-desc">{sample.data.description}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      <Form.Group className="mb-3">
        <Form.Label>Playbook Title</Form.Label>
        <Form.Control
          type="text"
          value={config.title || ''}
          onChange={(e) => updateConfig({ title: e.target.value })}
          placeholder="e.g. Payer Playbook — Cost vs Outcome Optimization"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          type="text"
          value={config.description || ''}
          onChange={(e) => updateConfig({ description: e.target.value })}
          placeholder="Evaluate pathways and make a decision based on tradeoffs."
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Section Labels <small className="text-muted">(optional — override the generic labels for a domain-specific feel)</small></Form.Label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, padding: '10px 12px', background: 'var(--surface-page)', borderRadius: 8, border: '1px solid var(--border-default)' }}>
          <div>
            <label className="drs-mini-label">Approach label</label>
            <Form.Control size="sm" value={config.labels?.approach || ''} onChange={(e) => updateConfig({ labels: { ...(config.labels || {}), approach: e.target.value } })} placeholder="Approach" />
          </div>
          <div>
            <label className="drs-mini-label">Sequence label</label>
            <Form.Control size="sm" value={config.labels?.sequence || ''} onChange={(e) => updateConfig({ labels: { ...(config.labels || {}), sequence: e.target.value } })} placeholder="Sequence" />
          </div>
          <div>
            <label className="drs-mini-label">Workflow label</label>
            <Form.Control size="sm" value={config.labels?.workflow || ''} onChange={(e) => updateConfig({ labels: { ...(config.labels || {}), workflow: e.target.value } })} placeholder="Workflow" />
          </div>
        </div>
        <Form.Text className="text-muted" style={{ fontSize: 11 }}>
          For a clinical playbook you might use "Clinical Approach" / "Sequence of care" / "Clinical Workflow". For an engineering playbook, "Design Rationale" / "Build Sequence" / "System Flow".
        </Form.Text>
      </Form.Group>

      {/* Identity & governance: condition binding, lineage, base promotion */}
      <div className="cs-sect">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Identity &amp; Governance</span>
          <span className="cs-sect-hint">which condition this decision resource implements, its lineage, and its role in the reference/copy model</span>
        </div>
        <div className="cs-grid-2">
          <div>
            <label className="drs-mini-label">Condition (Clinical Domains catalog)</label>
            {(clinicalMasters?.conditions?.length ?? 0) > 0 ? (
              <Form.Select
                size="sm"
                value={config.condition || ""}
                onChange={(e) => updateConfig({ condition: e.target.value || undefined })}
              >
                <option value="">— untagged —</option>
                {clinicalMasters!.conditions.map((c) => (
                  <option key={c.conditionKey} value={c.conditionKey}>
                    {c.name}{c.domains.find((d) => d.isPrimary) ? ` (${c.domains.find((d) => d.isPrimary)!.domainKey})` : ""}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <div className="drs-hint" style={{ margin: 0 }}>Clinical masters unavailable — condition tag: <code>{config.condition || "none"}</code></div>
            )}
          </div>
          <div>
            <label className="drs-mini-label">Lineage</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", minHeight: 31 }}>
              {config.isBaseTemplate && (
                <span className="drs-chip drs-chip-base">SynerGen reference v{config.templateVersion ?? 1} · {config.templateKey}</span>
              )}
              {!config.isBaseTemplate && config.basedOn && (
                <span className="drs-chip">from {config.basedOn.templateKey ?? `#${config.basedOn.resourceId}`}{config.basedOn.version ? ` v${config.basedOn.version}` : ""}</span>
              )}
              {!config.isBaseTemplate && !config.basedOn && <span className="drs-hint" style={{ margin: 0 }}>standalone — not derived from a base</span>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {!config.isBaseTemplate ? (
            <Button
              type="button" size="sm" variant="primary"
              disabled={promoteBlockers.length > 0}
              title={promoteBlockers.length > 0
                ? `Blocked by the computed gate:\n• ${promoteBlockers.join("\n• ")}`
                : "Flag this decision resource as a governed SynerGen reference — it will appear in 'SynerGen reference resources' and client copies will start from it"}
              onClick={() => {
                const suggested = `synergen-${config.condition || "resource"}-reference`;
                const key = window.prompt("Template key for this base:", config.templateKey || suggested);
                if (!key?.trim()) return;
                updateConfig({ isBaseTemplate: true, templateKey: key.trim(), templateVersion: config.templateVersion ?? 1 });
              }}
            >
              ★ Promote to SynerGen reference…
            </Button>
          ) : (
            <>
              <Button
                type="button" size="sm" variant="primary"
                title="Bump the reference edition — copies made afterwards record the new version in their lineage"
                onClick={() => {
                  const next = (config.templateVersion ?? 1) + 1;
                  if (window.confirm(`Bump ${config.templateKey} to v${next}? Existing copies keep their recorded lineage; new copies will record v${next}.`)) {
                    updateConfig({ templateVersion: next });
                  }
                }}
              >
                Bump version → v{(config.templateVersion ?? 1) + 1}
              </Button>
              <Button
                type="button" size="sm" variant="secondary"
                onClick={() => {
                  if (window.confirm("Demote from SynerGen reference? It disappears from 'SynerGen reference resources'; existing copies keep their lineage.")) {
                    updateConfig({ isBaseTemplate: undefined, templateKey: undefined, templateVersion: undefined });
                  }
                }}
              >
                Demote from reference
              </Button>
            </>
          )}
        </div>
      </div>

      {/* §4 publication boundary — draft → publish lifecycle */}
      <div className="cs-sect">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Publication</span>
          <span className="cs-sect-hint">learners execute the published edition; edits stay draft until republished (§4, §13.5)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {!config.publication && (
            <span className="drs-chip drs-chip-warn" title="Pre-lifecycle behavior: whatever is saved is what learners execute. Publishing opts this resource into the enforced draft → publish lifecycle.">
              Never published — executes live-as-saved
            </span>
          )}
          {config.publication?.status === "published" && (
            <span className="drs-chip drs-chip-base">
              Published v{config.publication.version}
              {config.publication.publishedAt ? ` · ${new Date(config.publication.publishedAt).toLocaleDateString()}` : ""}
            </span>
          )}
          {config.publication?.status === "draft" && (
            <span className="drs-chip drs-chip-warn" title="This working copy has changed since the last publish. Learners keep executing the published edition until you republish.">
              Draft — modified since v{config.publication.version}
            </span>
          )}
          <Button
            type="button" size="sm" variant="primary"
            disabled={publishBlockers.length > 0 || config.publication?.status === "published"}
            title={publishBlockers.length > 0
              ? `Blocked by the publication gate (§13.5):\n• ${publishBlockers.join("\n• ")}`
              : config.publication?.status === "published"
                ? "Already published — editing creates a new draft to publish"
                : "Snapshot this configuration as the edition learners execute"}
            onClick={publishResource}
          >
            Publish v{(config.publication?.version ?? 0) + 1}
          </Button>
          <span style={{ fontSize: 11, color: "var(--text-faint)" }}>Save the resource after publishing to persist it.</span>
        </div>
      </div>

      {/* §13.1 profile, §13.5 publication gate, §7 client-configuration ledger */}
      <GovernanceSections spec={spec} update={updateSpec} computedGate={computedGate} derived={derived} />
    </div>
  );

  // ────────────────── Approved Clinical Sources portlet (§5) ──────────────────
  const renderSources = () => (
    <SourcesSpecSections spec={spec} update={updateSpec} sourceRegistry={sourceRegistry} />
  );

  // ─────────── Client Formulary & Preferences portlet (§7 / §9.3) ───────────
  const renderFormulary = () => (
    <FormularySpecSections spec={spec} update={updateSpec} medicationClasses={masterMedClasses} derived={derived} />
  );

  // ─────────── Testing & Validation portlet (§13.5 / §18.2) ───────────
  const renderTesting = () => (
    <TestingValidationSections
      spec={spec}
      update={updateSpec}
      computedGate={computedGate}
      machineChecks={[
        { label: "Workflow machine & guardrails", ...computedGate.pg_guardrails },
        { label: "Escalation routes", ...computedGate.pg_escalation },
      ]}
    />
  );

  // ──────────────────── Pathway Specification portlet ────────────────────
  const updatePathwayField = (pi: number, field: string, value: any) => {
    const pw = [...pathways];
    if (pw[pi]) {
      const reclass = derived && pw[pi].ruleClass !== "A" && field !== "ruleClass" ? { ruleClass: "C" } : {};
      pw[pi] = { ...pw[pi], [field]: value, ...reclass };
      updateConfig({ pathways: pw });
    }
  };

  const addPathway = () => {
    const pw = [...pathways];
    const ids = 'ABCDEFGH';
    const nextId = ids[pw.length] || String(pw.length + 1);
    const values: any = {};
    attrs.forEach((a: any) => { values[a.name] = ''; });
    pw.push({ id: nextId, label: `Pathway ${nextId}`, title: '', steps: [], description: '', values });
    updateConfig({ pathways: pw });
  };

  const removePathway = (pi: number) => {
    updateConfig({ pathways: pathways.filter((_: any, i: number) => i !== pi) });
  };

  // AUTO-SYNC: every typeClassification edit merges the artifact patch AND
  // rewrites the bound capture fields' options in ONE updateConfig write — the
  // artifact is the single source of truth; the machine mirror cannot drift.
  // (The publish lifecycle is the gate that protects learners, so no manual
  // sync step is needed between artifact and machine.)
  const updateTypeClassification = (patch: Partial<NonNullable<ClinicalSpec["typeClassification"]>>) => {
    const prevTc = spec.typeClassification || {};
    const reclass = derived && prevTc.ruleClass !== "A" && !("ruleClass" in patch) ? { ruleClass: "C" as const } : {};
    const nextTc = { ...prevTc, ...patch, ...reclass };
    const updates: any = { clinicalSpec: { ...spec, typeClassification: nextTc } };
    if (def) {
      const hasField = (id?: string) => !!id && def.states.some((s) => (s.capture ?? []).some((c) => c.id === id));
      const typeFieldId = nextTc.typeCaptureFieldId || (hasField("etiology_type") ? "etiology_type" : undefined);
      const indicatorFieldId = nextTc.captureFieldId || (hasField("etiology_indicators") ? "etiology_indicators" : undefined);
      if (typeFieldId || indicatorFieldId) {
        updates.workflow = {
          ...def,
          states: def.states.map((s) => ({
            ...s,
            capture: (s.capture ?? []).map((c) => {
              if (indicatorFieldId && c.id === indicatorFieldId) {
                return { ...c, type: "multiselect" as CaptureType, options: nextTc.suspicionIndicators || [], placeholder: undefined };
              }
              if (typeFieldId && c.id === typeFieldId) {
                return { ...c, type: "select" as CaptureType, options: nextTc.types || [], placeholder: undefined };
              }
              return c;
            }),
          })),
        };
      }
    }
    updateConfig(updates);
  };

  const renderPathways = () => (
    <div>
      {/* §8 — governed clinical specification (classification, type classification, pathway logic) */}
      <PathwaySpecSections spec={spec} update={updateSpec} derived={derived} onTcChange={updateTypeClassification} workflowDef={def} parameters={masterParameters} />

      <div className="cs-sect">
      <div className="cs-sect-head">
        <span className="cs-sect-title">Operational Pathways</span>
        <SpecRef r="§8.2 / §8.3 Step 4" />
        <span className="cs-sect-hint">one card per pathway — governed conditions/actions plus what the learner sees in Explore; the comparison layer below is iLearn's Compare table</span>
      </div>
      {(spec.pathwayMappingDimensions || []).length > 0 && (
        <div className="cs-static-chips" style={{ marginBottom: 8 }}>
          <span className="cs-inline-label" style={{ margin: 0 }}>Each pathway maps to:</span>
          {spec.pathwayMappingDimensions!.map((d) => <span key={d} className="drs-chip">{d}</span>)}
        </div>
      )}
      {/* Master–detail: compact selector strip (always browsable) + one full editor */}
      <div className="drs-pw-strip">
        {pathways.map((p: any, i: number) => {
          const color = PATHWAY_COLORS[i % 4] || '#6b7280';
          const conds = (p.referenceConditions || []).length;
          const acts = (p.referenceActions || []).length;
          const activeIdx = Math.min(activePathwayIdx, Math.max(0, pathways.length - 1));
          return (
            <button key={i} type="button"
              className={"drs-pw-card" + (i === activeIdx ? " active" : "")}
              style={{ borderLeftColor: color }}
              onClick={() => setActivePathwayIdx(i)}>
              <span className="drs-pw-card-label" style={{ color }}>
                {p.label || `Pathway ${i + 1}`} <ClassBadge c={p.ruleClass} />
              </span>
              <span className="drs-pw-card-title">{p.title || "—"}</span>
              <span className="drs-pw-card-meta">
                {conds} condition{conds === 1 ? "" : "s"} · {acts} action{acts === 1 ? "" : "s"}
                {(conds === 0 || acts === 0) && (
                  <span className="drs-pw-card-warn" title="Reference conditions or actions are empty">⚠</span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <DeploymentLock
        derived={derived}
        overridden={pathways.some((p: any) => p.ruleClass === "C")}
        what="the operational pathways"
      >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {pathways.length > 0 && (() => {
          const pi = Math.min(activePathwayIdx, pathways.length - 1);
          const p: any = pathways[pi];
          return (
          <div key={pi} style={{ border: '1px solid var(--border-default)', borderLeft: `4px solid ${PATHWAY_COLORS[pi % 4] || '#6b7280'}`, borderRadius: 8, padding: '12px 14px', background: 'var(--surface-page)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: PATHWAY_COLORS[pi % 4] || '#6b7280' }}>{p.label || `Pathway ${pi + 1}`}</span>
              <span style={{ marginLeft: 'auto' }}>
                <ClassControl value={p.ruleClass} onChange={(c) => updatePathwayField(pi, 'ruleClass', c)} derived={derived} />
              </span>
              {pathways.length > 2 && (
                <button type="button" onClick={() => { removePathway(pi); setActivePathwayIdx(Math.max(0, pi - 1)); }} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}>×</button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label className="drs-mini-label">Label</label>
                <Form.Control size="sm" value={p.label || ''} onChange={(e) => updatePathwayField(pi, 'label', e.target.value)} />
              </div>
              <div>
                <label className="drs-mini-label">Title</label>
                <Form.Control size="sm" value={p.title || ''} onChange={(e) => updatePathwayField(pi, 'title', e.target.value)} placeholder="e.g. Conservative Management" />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <label className="drs-mini-label">When to use <small style={{ fontWeight: 400, textTransform: 'none' as const }}>(shown in Explore — short "best for…" line)</small></label>
              <Form.Control size="sm" value={p.indications || ''} onChange={(e) => updatePathwayField(pi, 'indications', e.target.value)} placeholder="Best for: ..." />
            </div>
            <div style={{ marginTop: 8 }}>
              <label className="drs-mini-label">Approach <small style={{ fontWeight: 400, textTransform: 'none' as const }}>(shown in Explore — narrative reasoning, no numbers)</small></label>
              <Form.Control as="textarea" rows={3} value={p.rationale || ''} onChange={(e) => updatePathwayField(pi, 'rationale', e.target.value)} placeholder="Explain the philosophy of this approach. Why does it work? What makes it the right call in some scenarios?" style={{ fontSize: 12 }} />
            </div>
            <div style={{ marginTop: 8 }}>
              <label className="drs-mini-label">Steps <small style={{ fontWeight: 400, textTransform: 'none' as const }}>(comma-separated)</small></label>
              <Form.Control size="sm" value={Array.isArray(p.steps) ? p.steps.join(', ') : ''} onChange={(e) => updatePathwayField(pi, 'steps', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} placeholder="Step 1, Step 2, Step 3" />
            </div>
            <div style={{ marginTop: 8 }}>
              <label className="drs-mini-label">Description <small style={{ fontWeight: 400, textTransform: 'none' as const }}>(one-line summary)</small></label>
              <Form.Control as="textarea" rows={2} value={p.description || ''} onChange={(e) => updatePathwayField(pi, 'description', e.target.value)} style={{ fontSize: 12 }} />
            </div>
            <div className="cs-grid-2" style={{ marginTop: 8 }}>
              <div>
                <label className="drs-mini-label">Reference conditions <small style={{ fontWeight: 400, textTransform: 'none' as const }}>(when a patient belongs here — governed)</small></label>
                <ListEditor items={p.referenceConditions || []} onChange={(v) => updatePathwayField(pi, 'referenceConditions', v)} placeholder="condition…" disabled={derived && p.ruleClass === 'A'} />
              </div>
              <div>
                <label className="drs-mini-label">Reference actions <small style={{ fontWeight: 400, textTransform: 'none' as const }}>(what this pathway commits to — governed)</small></label>
                <ListEditor items={p.referenceActions || []} onChange={(v) => updatePathwayField(pi, 'referenceActions', v)} placeholder="action…" disabled={derived && p.ruleClass === 'A'} />
              </div>
            </div>
          </div>
          );
        })()}
        {pathways.length < 4 && (
          <Button type="button" variant="secondary" size="sm"
            onClick={() => { addPathway(); setActivePathwayIdx(pathways.length); }}
            style={{ alignSelf: 'flex-start' }}>
            + Add Pathway
          </Button>
        )}
      </div>
      </DeploymentLock>

      {renderComparisonAttributes()}
      </div>

      {/* §8.3 executable decision sequence — last, mirroring the PDF's §8.1 → §8.2 → §8.3 order */}
      <div className="cs-sect">
        <WorkflowBuilder
          workflow={config.workflow}
          onChange={(wf) => updateConfig({ workflow: wf })}
          decisionSteps={(spec.decisionSequence || []).map((s) => ({ step: s.step, name: s.name }))}
          actorOptions={knownRoles}
          guardrailOptions={(spec.guardrails?.rules ?? []).filter((g) => g.id && g.trigger).map((g) => ({ id: g.id!, trigger: g.trigger }))}
        />
      </div>

      {/* Live §8.3 sequence overview — on-demand slide-over so editors keep full width */}
      {!seqOverlayOpen && (
        <button type="button" className="drs-seq-fab" title="Live §8.3 sequence overview — how the spec steps map to workflow states"
          onClick={() => setSeqOverlayOpen(true)}>
          Sequence overview
        </button>
      )}
      {seqOverlayOpen && (
        <div className="drs-seq-overlay">
          <button type="button" className="drs-del drs-seq-overlay-close" title="Close overview" onClick={() => setSeqOverlayOpen(false)}>×</button>
          <DecisionSequenceMap spec={spec} workflowDef={def} />
        </div>
      )}
    </div>
  );

  // ─────────────────────── Medication Decision Matrix portlet ───────────────────────
  const updatePathwayValue = (pi: number, attrName: string, value: any) => {
    const pw = [...pathways];
    if (pw[pi]) {
      if (!pw[pi].values) pw[pi].values = {};
      pw[pi].values[attrName] = value;
      updateConfig({ pathways: pw });
    }
  };

  const addAttribute = () => {
    updateConfig({ attributes: [...attrs, { name: '', unit: '', direction: 'higher-is-better' }] });
  };

  const removeAttribute = (ai: number) => {
    const removed = attrs[ai];
    const newAttrs = attrs.filter((_: any, i: number) => i !== ai);
    // Also remove this attribute's value from all pathways
    const pw = pathways.map((p: any) => {
      if (p.values && removed.name) { const v = { ...p.values }; delete v[removed.name]; return { ...p, values: v }; }
      return p;
    });
    updateConfig({ attributes: newAttrs, pathways: pw });
  };

  const updateAttribute = (ai: number, field: string, value: any) => {
    const newAttrs = [...attrs];
    const oldName = newAttrs[ai].name;
    newAttrs[ai] = { ...newAttrs[ai], [field]: value };
    // If name changed, rename the key in all pathway values
    if (field === 'name' && oldName && oldName !== value) {
      const pw = pathways.map((p: any) => {
        if (p.values && p.values[oldName] !== undefined) {
          const v = { ...p.values };
          v[value] = v[oldName];
          delete v[oldName];
          return { ...p, values: v };
        }
        return p;
      });
      updateConfig({ attributes: newAttrs, pathways: pw });
    } else {
      updateConfig({ attributes: newAttrs });
    }
  };

  // §9 — the Medication Decision Matrix portlet (spec-shaped; the generic
  // attributes × pathways comparison grid lives in Pathway Specification,
  // where §13.2 places "Attributes").
  const renderMatrix = () => (
    <DeploymentLock
      derived={derived}
      overridden={(spec.medicationMatrix?.rows ?? []).some((r) => r.ruleClass === "C")}
      what="the medication decision matrix"
    >
      <MedicationMatrixSections spec={spec} update={updateSpec} parameters={masterParameters} medicationClasses={masterMedClasses} derived={derived} />
    </DeploymentLock>
  );

  const renderComparisonAttributes = () => (
    <div className="cs-sect">
      <div className="cs-sect-head">
        <span className="cs-sect-title">Pathway Comparison</span>
        <SpecRef r="§13.2" />
        <span className="cs-sect-hint">manifests in iLearn as the Compare table (Explore → Compare → Decide journey): attributes are its rows, pathways its columns; a learner-built custom pathway joins as an extra column</span>
      </div>
      <Form.Label>Attributes <small className="text-muted">(the dimensions learners weigh)</small></Form.Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, padding: '10px 12px', background: 'var(--surface-page)', borderRadius: 8, border: '1px solid var(--border-default)' }}>
        {attrs.map((attr: any, ai: number) => (
          <div key={ai} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Form.Control size="sm" value={attr.name || ''} onChange={(e) => updateAttribute(ai, 'name', e.target.value)} placeholder="Name (e.g. Cost)" style={{ flex: 2 }} />
            <Form.Control size="sm" value={attr.unit || ''} onChange={(e) => updateAttribute(ai, 'unit', e.target.value)} placeholder="Unit" style={{ flex: 1, maxWidth: 70 }} />
            <Form.Select size="sm" value={attr.direction || 'higher-is-better'} onChange={(e) => updateAttribute(ai, 'direction', e.target.value)} style={{ flex: 2 }}>
              <option value="higher-is-better">Higher is better</option>
              <option value="lower-is-better">Lower is better</option>
            </Form.Select>
            {attrs.length > 1 && (
              <button type="button" onClick={() => removeAttribute(ai)} style={{ background: 'none', border: 'none', color: 'var(--status-danger)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
            )}
          </div>
        ))}
        <Button type="button" variant="secondary" size="sm" onClick={addAttribute} style={{ alignSelf: 'flex-start', fontSize: 11 }}>
          + Add Attribute
        </Button>
      </div>

      {attrs.length === 0 || pathways.length === 0 ? (
        <div className="drs-hint">Add at least one attribute and one pathway to build the matrix.</div>
      ) : (
        <>
          <Form.Label>Matrix <small className="text-muted">(each pathway's value per attribute — rendered as iLearn's Compare table)</small></Form.Label>
          <div className="drs-matrix-wrap">
            <table className="drs-matrix">
              <thead>
                <tr>
                  <th>Attribute</th>
                  {pathways.map((p: any, pi: number) => (
                    <th key={pi} style={{ color: PATHWAY_COLORS[pi % 4] || '#6b7280' }}>
                      {p.label || `Pathway ${pi + 1}`}
                      {p.title && <span className="drs-matrix-sub">{p.title}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attrs.map((attr: any, ai: number) => (
                  <tr key={ai}>
                    <td className="drs-matrix-attr">
                      {attr.name || `Attribute ${ai + 1}`}
                      <span className="drs-matrix-sub">
                        {attr.unit ? `${attr.unit} · ` : ''}
                        {attr.direction === 'lower-is-better' ? '↓ lower is better' : '↑ higher is better'}
                      </span>
                    </td>
                    {pathways.map((p: any, pi: number) => (
                      <td key={pi}>
                        <Form.Control
                          size="sm"
                          value={p.values?.[attr.name] ?? ''}
                          onChange={(e) => updatePathwayValue(pi, attr.name, e.target.value)}
                          placeholder="—"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  // ────────────────── Safety & Guardrails portlet (projection) ──────────────────
  const renderGuardrails = () => {
    const specSections = (
      <GovernanceRecord specRef="§10" lockEdits={!derived}
        hint="Audited reference content — precedence, the 13-guardrail table, class checks. Rows with an 'Enforced by' transition link are machine-enforced; the rest are declared for SME review.">
        <DeploymentLock
          derived={derived}
          overridden={(spec.guardrails?.rules ?? []).some((r) => r.ruleClass === "C")}
          what="the safety guardrails"
        >
          <GuardrailSpecSections
            spec={spec}
            update={updateSpec}
            workflowTransitions={def?.transitions ?? []}
            parameters={masterParameters}
            medicationClasses={masterMedClasses}
            derived={derived}
          />
        </DeploymentLock>
      </GovernanceRecord>
    );
    if (!def) {
      return (
        <div>
          {renderNeedsStateMachine("Hard stops, safety exits and review checkpoints")}
          {specSections}
        </div>
      );
    }

    const guarded = def.transitions
      .map((t, ti) => ({ t, ti }))
      .filter(({ t }) => t.guard);
    const unguardedAdvances = def.transitions
      .map((t, ti) => ({ t, ti }))
      .filter(({ t }) => !t.guard && t.kind === "advance");
    const exitRows = def.transitions
      .map((t, ti) => ({ t, ti }))
      .filter(({ t }) => t.kind !== "advance");
    const statesMissingExit = def.states.filter(
      (s) =>
        (s.kind === "task" || s.kind === "decision") &&
        !def.transitions.some((t) => t.from === s.id && t.kind !== "advance"),
    );

    const guardEditor = ({ t, ti }: { t: WorkflowTransition; ti: number }) => (
      <div className="drs-rule-edit">
        <Form.Select size="sm" style={{ maxWidth: 170 }} value={t.guard?.type ?? "none"} onChange={(e) => setTransitionGuard(ti, e.target.value)}>
          <option value="none">No rule</option>
          <option value="manual">Manual (authorized click)</option>
          <option value="field">Field condition</option>
          <option value="aiReview">Human review ack</option>
        </Form.Select>
        {t.guard?.type === "field" && (
          <>
            <Form.Select size="sm" style={{ maxWidth: 160 }} value={t.guard.field} onChange={(e) => updateTransition(ti, { guard: { ...(t.guard as FieldGuard), field: e.target.value } })}>
              <option value="">— field —</option>
              {allCaptureFields.map((f) => <option key={f.id} value={f.id}>{f.id}</option>)}
            </Form.Select>
            <Form.Select size="sm" style={{ maxWidth: 110 }} value={t.guard.op} onChange={(e) => updateTransition(ti, { guard: { ...(t.guard as FieldGuard), op: e.target.value as FieldOp } })}>
              {FIELD_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
            </Form.Select>
            {t.guard.op !== "notEmpty" && (
              <Form.Control
                size="sm"
                style={{ maxWidth: 140 }}
                value={Array.isArray(t.guard.value) ? t.guard.value.join(", ") : String(t.guard.value ?? "")}
                placeholder={t.guard.op === "in" ? "a, b, c" : "value"}
                onChange={(e) => {
                  const fg = t.guard as FieldGuard;
                  const raw = e.target.value;
                  const value = fg.op === "in"
                    ? raw.split(",").map((v) => v.trim()).filter(Boolean)
                    : (raw !== "" && !isNaN(Number(raw)) ? Number(raw) : raw);
                  updateTransition(ti, { guard: { ...fg, value } });
                }}
              />
            )}
          </>
        )}
        {t.guard?.type === "manual" && (
          <Form.Control
            size="sm"
            style={{ maxWidth: 220 }}
            value={(t.guard.roles ?? []).join(", ")}
            placeholder="roles (empty = anyone)"
            onChange={(e) => updateTransition(ti, { guard: { type: "manual", roles: e.target.value.split(",").map((r) => r.trim()).filter(Boolean) } })}
          />
        )}
      </div>
    );

    return (
      <div>
        <div className="drs-section-label">Workflow enforcement <small>(executable — what the server actually blocks)</small></div>
        <div className="drs-stat-row">
          <div className="drs-stat"><span className="drs-stat-n">{hardStops.length}</span> hard stops<small>field rules that block advancing</small></div>
          <div className="drs-stat"><span className="drs-stat-n">{checkpoints.length}</span> review checkpoints<small>require human acknowledgment</small></div>
          <div className="drs-stat"><span className="drs-stat-n">{safetyExits.length}</span> safety exits<small>hold / escalate / return paths</small></div>
          <div className="drs-stat"><span className="drs-stat-n">{restricted.length}</span> restricted actions<small>role-limited transitions</small></div>
        </div>

        {statesMissingExit.length > 0 && (
          <div className="drs-lint">
            <strong>⚠ Safety review</strong>
            <ul>
              {statesMissingExit.map((s) => (
                <li key={s.id}>
                  <b>{s.label}</b> has no safety exit — a case in this step cannot hold, escalate, or return. Add a
                  non-advance transition in Pathway Specification if this step can go wrong.
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="drs-section-label">Enforced rules <small>(server-evaluated — the UI cannot bypass these)</small></div>
        {guarded.length === 0 && <div className="drs-hint">No guarded transitions yet. Add a rule to an action below, or edit transitions in Pathway Specification.</div>}
        {guarded.map(({ t, ti }) => (
          <div key={ti} className="drs-rule-card">
            <div className="drs-rule-head">
              <span className={`drs-kind drs-kind-${t.kind}`}>{KIND_BADGE[t.kind]?.icon} {t.kind}</span>
              <span className="drs-rule-route">{stateLabel(t.from)} → {stateLabel(t.to)}</span>
              <span className="drs-rule-action">"{t.label}"</span>
            </div>
            <div className="drs-rule-sentence">{guardSentence(t.guard)}</div>
            {guardEditor({ t, ti })}
          </div>
        ))}

        {unguardedAdvances.length > 0 && (
          <>
            <div className="drs-section-label">Unguarded advances <small>(anyone can move the case forward here)</small></div>
            {unguardedAdvances.map(({ t, ti }) => (
              <div key={ti} className="drs-rule-card drs-rule-card-muted">
                <div className="drs-rule-head">
                  <span className={`drs-kind drs-kind-${t.kind}`}>{KIND_BADGE[t.kind]?.icon} {t.kind}</span>
                  <span className="drs-rule-route">{stateLabel(t.from)} → {stateLabel(t.to)}</span>
                  <span className="drs-rule-action">"{t.label}"</span>
                </div>
                {guardEditor({ t, ti })}
              </div>
            ))}
          </>
        )}

        <div className="drs-section-label">Safety exits <small>(always available — required-field checks do not apply)</small></div>
        {exitRows.length === 0 && <div className="drs-hint">No hold / escalate / return paths defined.</div>}
        {exitRows.map(({ t, ti }) => (
          <div key={ti} className="drs-rule-card drs-rule-card-muted">
            <div className="drs-rule-head">
              <span className={`drs-kind drs-kind-${t.kind}`}>{KIND_BADGE[t.kind]?.icon} {t.kind}</span>
              <span className="drs-rule-route">{stateLabel(t.from)} → {stateLabel(t.to)}</span>
              <span className="drs-rule-action">"{t.label}"</span>
              <span className="drs-rule-sentence" style={{ marginLeft: "auto" }}>{guardSentence(t.guard)}</span>
            </div>
          </div>
        ))}

        {specSections}
      </div>
    );
  };

  // ──────────────── Authority & Escalation portlet (projection) ────────────────
  const toggleRole = (ti: number, role: string) => {
    if (!def) return;
    const t = def.transitions[ti];
    const roles = t.guard?.type === "manual" ? [...(t.guard.roles ?? [])] : [];
    const idx = roles.indexOf(role);
    if (idx >= 0) roles.splice(idx, 1);
    else roles.push(role);
    updateTransition(ti, { guard: { type: "manual", roles } });
  };

  const renderAuthority = () => {
    const specSections = (
      <GovernanceRecord specRef="§11" lockEdits={!derived}
        hint="Audited reference content — the 11-row trigger matrix and authority scopes. Triggers/behaviors are declared text for SME review; WHO may act is enforced by the roles & guards above; deployment values (CCR) route humans and are resolved on client copies.">
        <AuthoritySpecSections spec={spec} update={updateSpec} />
      </GovernanceRecord>
    );
    if (!def) {
      return (
        <div>
          {renderNeedsStateMachine("Role authority and escalation routes")}
          {specSections}
        </div>
      );
    }

    const rows = def.transitions.map((t, ti) => ({ t, ti }));
    const escalations = rows.filter(({ t }) => t.kind === "escalate" || t.kind === "return");

    return (
      <div>
        <div className="drs-section-label">Workflow enforcement <small>(executable — manual-guard roles the server checks)</small></div>
        <div className="drs-section-label">Roles</div>
        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
          {knownRoles.map((r) => <span key={r} className="drs-role-chip">{r}</span>)}
          {knownRoles.length === 0 && <span className="drs-hint" style={{ margin: 0 }}>No roles yet — add one to start restricting actions.</span>}
          <Form.Control
            size="sm"
            style={{ maxWidth: 160 }}
            value={newRole}
            placeholder="e.g. pharmacist"
            onChange={(e) => setNewRole(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const r = newRole.trim();
                if (r && !knownRoles.includes(r)) setExtraRoles([...extraRoles, r]);
                setNewRole("");
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!newRole.trim() || knownRoles.includes(newRole.trim())}
            onClick={() => { setExtraRoles([...extraRoles, newRole.trim()]); setNewRole(""); }}
          >
            + Add role
          </Button>
        </div>

        <div className="drs-section-label">Authority matrix <small>(who may perform each action — writes the manual-guard roles the server enforces)</small></div>
        <div className="drs-matrix-wrap">
          <table className="drs-matrix drs-authority">
            <thead>
              <tr>
                <th>Action</th>
                {knownRoles.map((r) => <th key={r}>{r}</th>)}
                <th>Anyone</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ t, ti }) => {
                const editable = !t.guard || t.guard.type === "manual";
                const roles = t.guard?.type === "manual" ? t.guard.roles ?? [] : [];
                const anyone = editable && roles.length === 0;
                return (
                  <tr key={ti}>
                    <td className="drs-matrix-attr">
                      <span className={`drs-kind drs-kind-${t.kind}`}>{KIND_BADGE[t.kind]?.icon}</span> {t.label}
                      <span className="drs-matrix-sub">{stateLabel(t.from)} → {stateLabel(t.to)}</span>
                    </td>
                    {editable ? (
                      <>
                        {knownRoles.map((r) => (
                          <td key={r} className="drs-check-cell">
                            <Form.Check type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(ti, r)} />
                          </td>
                        ))}
                        <td className="drs-check-cell">{anyone ? <span className="drs-anyone">anyone</span> : ""}</td>
                      </>
                    ) : (
                      <td colSpan={knownRoles.length + 1} className="drs-governed-note">
                        governed by {t.guard?.type === "field" ? "a field rule" : "human-review acknowledgment"} — manage in Safety &amp; Guardrails
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="drs-section-label">Step actors <small>(display metadata — labels each step with who performs it in iLearn; does NOT restrict who may act — that is the authority matrix above)</small></div>
        <div className="drs-actor-grid">
          {def.states.map((s, si) => {
            const actorOptions = Array.from(new Set(["system", ...knownRoles]));
            return (
              <React.Fragment key={s.id}>
                <span className="drs-actor-state">{s.label}</span>
                <Form.Select
                  size="sm"
                  value={s.actor || ""}
                  onChange={(e) => updateWfState(si, { actor: e.target.value || undefined })}
                >
                  <option value="">— unassigned —</option>
                  {s.actor && !actorOptions.includes(s.actor) && (
                    <option value={s.actor}>{s.actor} ⚠ (not in roles master)</option>
                  )}
                  {actorOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                </Form.Select>
              </React.Fragment>
            );
          })}
        </div>

        <div className="drs-section-label">Escalation routes</div>
        {escalations.length === 0 && <div className="drs-hint">No escalate / return transitions defined.</div>}
        {escalations.map(({ t, ti }) => (
          <div key={ti} className="drs-rule-card drs-rule-card-muted">
            <div className="drs-rule-head">
              <span className={`drs-kind drs-kind-${t.kind}`}>{KIND_BADGE[t.kind]?.icon} {t.kind}</span>
              <span className="drs-rule-route">{stateLabel(t.from)} → {stateLabel(t.to)}</span>
              <span className="drs-rule-action">"{t.label}"</span>
              <span className="drs-rule-sentence" style={{ marginLeft: "auto" }}>{guardSentence(t.guard)}</span>
            </div>
          </div>
        ))}

        {specSections}
      </div>
    );
  };

  // ─────────────── Monitoring & Outcomes portlet (projection) ───────────────
  const renderMonitoring = () => {
    const specSections = (
      <GovernanceRecord specRef="§12" lockEdits={!derived}
        hint="Audited reference content — baseline evidence, medication monitoring, follow-up and outcome-capture lists. Declared for SME review; what the case actually records is the workflow capture above.">
        <MonitoringSpecSections spec={spec} update={updateSpec} parameters={masterParameters} />
      </GovernanceRecord>
    );
    if (!def) {
      return (
        <div>
          {renderNeedsStateMachine("Per-step data capture and outcome fields")}
          {specSections}
        </div>
      );
    }

    const guardsOnField = (fieldId: string) =>
      def.transitions.filter((t) => t.guard?.type === "field" && t.guard.field === fieldId);

    const addCaptureField = (si: number) => {
      const st = def.states[si];
      const capture = [...(st.capture ?? [])];
      let id = `field_${capture.length + 1}`;
      while (capture.some((c) => c.id === id)) id = `${id}_x`;
      capture.push({ id, label: "New field", type: "text" });
      updateWfState(si, { capture });
    };
    const updateCaptureField = (si: number, ci: number, patch: Partial<WorkflowCaptureField>) => {
      const st = def.states[si];
      updateWfState(si, { capture: (st.capture ?? []).map((c, i) => (i === ci ? { ...c, ...patch } : c)) });
    };
    const removeCaptureField = (si: number, ci: number) => {
      const st = def.states[si];
      updateWfState(si, { capture: (st.capture ?? []).filter((_, i) => i !== ci) });
    };

    const requiredCount = allCaptureFields.filter((c) => c.required).length;
    const gatingCount = allCaptureFields.filter((c) => guardsOnField(c.id).length > 0).length;

    return (
      <div>
        <div className="drs-section-label">Workflow capture <small>(executable — fields the case actually records)</small></div>
        <div className="drs-stat-row">
          <div className="drs-stat"><span className="drs-stat-n">{totalCaptureFields}</span> capture fields<small>recorded into the case</small></div>
          <div className="drs-stat"><span className="drs-stat-n">{requiredCount}</span> required<small>block advancing when empty</small></div>
          <div className="drs-stat"><span className="drs-stat-n">{gatingCount}</span> drive rules<small>referenced by field guards</small></div>
        </div>

        {def.states.map((s, si) => (
          <div key={s.id} className="drs-rule-card">
            <div className="drs-rule-head">
              <span className="drs-rule-route">{s.label}</span>
              <span className="drs-matrix-sub" style={{ display: "inline", marginLeft: 8 }}>{s.kind}{s.actor ? ` · ${s.actor}` : ""}</span>
              <Button type="button" size="sm" variant="secondary" style={{ marginLeft: "auto" }} onClick={() => addCaptureField(si)}>+ Field</Button>
            </div>
            {(s.capture ?? []).length === 0 && <div className="drs-hint" style={{ marginTop: 6 }}>Nothing recorded in this step.</div>}
            {(s.capture ?? []).map((c, ci) => (
              <div key={ci} className="drs-capture-row">
                <Form.Control size="sm" style={{ flex: 2 }} value={c.label} placeholder="Label" onChange={(e) => updateCaptureField(si, ci, { label: e.target.value })} />
                <Form.Control size="sm" style={{ flex: 1.4 }} value={c.id} placeholder="id" onChange={(e) => updateCaptureField(si, ci, { id: e.target.value.trim() })} />
                <Form.Select size="sm" style={{ flex: 1.2 }} value={c.type} onChange={(e) => updateCaptureField(si, ci, { type: e.target.value as CaptureType })}>
                  {CAPTURE_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </Form.Select>
                {c.type === "select" && (
                  <Form.Control size="sm" style={{ flex: 2 }} value={(c.options ?? []).join(", ")} placeholder="options, comma-separated" onChange={(e) => updateCaptureField(si, ci, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })} />
                )}
                <Form.Check type="checkbox" label="req" checked={!!c.required} onChange={(e) => updateCaptureField(si, ci, { required: e.target.checked || undefined })} style={{ fontSize: 11 }} />
                {guardsOnField(c.id).length > 0 && (
                  <span className="drs-gate-badge" title={guardsOnField(c.id).map((t) => `"${t.label}" (${stateLabel(t.from)} → ${stateLabel(t.to)})`).join("\n")}>
                    🔒 gates {guardsOnField(c.id).length}
                  </span>
                )}
                <button type="button" className="drs-del" onClick={() => removeCaptureField(si, ci)}>×</button>
              </div>
            ))}
          </div>
        ))}

        {specSections}
      </div>
    );
  };

  // ─────────────────────── AI Workspace portlet ───────────────────────
  const renderAiWorkspace = () => (
    <div>
      <div style={{ background: 'var(--surface-page)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '4px 14px', marginBottom: 16 }}>
        <div className="sr-toggle-row">
          <div className="sr-toggle-meta">
            <div className="sr-toggle-title">Require student justification</div>
            <div className="sr-toggle-help">Students must explain their pathway choice in a free-text response</div>
          </div>
          <label className="sr-switch">
            <input
              type="checkbox"
              checked={!!config.showJustification}
              onChange={(e) => {
                const updates: any = { showJustification: e.target.checked };
                if (!e.target.checked) updates.enableAIScoring = false;
                updateConfig(updates);
              }}
            />
            <span className="sr-switch-slider" />
          </label>
        </div>
        <div className="sr-toggle-row sr-nested" style={{ opacity: config.showJustification ? 1 : 0.5 }}>
          <div className="sr-toggle-meta">
            <div className="sr-toggle-title">Enable AI scoring of justification</div>
            <div className="sr-toggle-help">Auto-score using the assigned rubric and evaluation criteria</div>
          </div>
          <label className="sr-switch">
            <input
              type="checkbox"
              checked={!!config.enableAIScoring}
              disabled={!config.showJustification}
              onChange={(e) => updateConfig({ enableAIScoring: e.target.checked })}
            />
            <span className="sr-switch-slider" />
          </label>
        </div>

        {/* Custom Pathway — let learners construct their own */}
        <div className="sr-toggle-row" style={{ borderTop: '1px solid var(--border-default)', paddingTop: 10, marginTop: 6 }}>
          <div className="sr-toggle-meta">
            <div className="sr-toggle-title">
              Allow learners to construct their own pathway
              <span
                title="When enabled, learners see a 'Construct your own pathway' option in addition to the configured pathways. They chat with Claude to build a custom pathway that joins the Compare tab as another column."
                style={{ marginLeft: 6, color: 'var(--text-faint)', cursor: 'help', fontWeight: 400, fontSize: 13 }}
              >&#9432;</span>
            </div>
            <div className="sr-toggle-help">Learner chats with Claude to build a custom pathway; it joins the Compare tab alongside your configured pathways</div>
          </div>
          <label className="sr-switch">
            <input
              type="checkbox"
              checked={!!config.customPathway?.enabled}
              onChange={(e) => {
                const cp = { ...(config.customPathway || {}), enabled: e.target.checked };
                // Default trustClaude to true on first enable
                if (e.target.checked && cp.trustClaude === undefined) cp.trustClaude = true;
                updateConfig({ customPathway: cp });
              }}
            />
            <span className="sr-switch-slider" />
          </label>
        </div>

        {config.customPathway?.enabled && (
          <>
            <div className="sr-toggle-row sr-nested">
              <div className="sr-toggle-meta">
                <div className="sr-toggle-title">
                  Trust Claude (no manual guardrails required)
                  <span
                    title="Claude will infer constraints from your configured pathways — the vocabulary of steps, metric ranges, and domain rigor. No manual rules needed. Uncheck this to require Claude to follow only the guardrails you write below."
                    style={{ marginLeft: 6, color: 'var(--text-faint)', cursor: 'help', fontWeight: 400, fontSize: 13 }}
                  >&#9432;</span>
                </div>
                <div className="sr-toggle-help">Claude infers constraints from your configured pathways. Uncheck to enforce only the guardrails you write below.</div>
              </div>
              <label className="sr-switch">
                <input
                  type="checkbox"
                  checked={config.customPathway?.trustClaude !== false}
                  onChange={(e) => {
                    const cp = { ...(config.customPathway || {}), trustClaude: e.target.checked };
                    updateConfig({ customPathway: cp });
                  }}
                />
                <span className="sr-switch-slider" />
              </label>
            </div>

            <div style={{ padding: '6px 0 4px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <Form.Label style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
                  Custom Pathway Guardrails
                  <span style={{ color: 'var(--text-faint)', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>(optional)</span>
                  <span
                    title="Plain-English rules Claude must follow when helping the learner construct a pathway. Example: 'Stay within 24–72hr discharge window. Never skip vitals monitoring. Only use medications on the formulary.' Click 'Suggest guardrails' to have Claude draft these from your configured pathways."
                    style={{ marginLeft: 6, color: 'var(--text-faint)', cursor: 'help', fontWeight: 400, fontSize: 13 }}
                  >&#9432;</span>
                </Form.Label>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={suggestingGuardrails || pathways.length === 0}
                  title={pathways.length === 0 ? 'Configure at least one pathway first' : 'Have Claude draft guardrails from your configured pathways'}
                  onClick={async () => {
                    setSuggestingGuardrails(true);
                    setSuggestGuardrailsError(null);
                    try {
                      const draft = await StudyResourceService.suggestPathwayGuardrails(config);
                      const cp = { ...(config.customPathway || {}), guardrails: draft };
                      updateConfig({ customPathway: cp });
                    } catch (err: any) {
                      setSuggestGuardrailsError(err?.response?.data?.error?.message || err?.message || 'Failed to suggest guardrails');
                    } finally {
                      setSuggestingGuardrails(false);
                    }
                  }}
                  style={{ fontSize: 12, padding: '3px 10px' }}
                >
                  {suggestingGuardrails ? (
                    <><Spinner size="sm" animation="border" style={{ width: 12, height: 12, marginRight: 6, borderWidth: 2 }} />Drafting…</>
                  ) : <>&#10024; Suggest guardrails</>}
                </Button>
              </div>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Optional. e.g. Stay within 24–72hr discharge window. Never skip vitals monitoring. Only use medications on the formulary."
                value={config.customPathway?.guardrails || ''}
                onChange={(e) => {
                  const cp = { ...(config.customPathway || {}), guardrails: e.target.value };
                  updateConfig({ customPathway: cp });
                }}
                style={{ fontSize: 13 }}
              />
              {suggestGuardrailsError && (
                <div style={{ fontSize: 11, color: '#c53030', marginTop: 4 }}>{suggestGuardrailsError}</div>
              )}
              <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                Leave blank if you trust Claude with default guardrails. Both can coexist — your text takes priority.
              </Form.Text>
            </div>
          </>
        )}
      </div>

      {/* §13.2–§13.4 — rubric levels, workspace settings, RAG sources, prompt set */}
      <GovernanceRecord specRef="§13.2–13.4" lockEdits={!derived}
        hint="Audited declarations — rubric levels, workspace settings, RAG source lists, the 14 prompt names. Not yet wired to a live workspace; the toggles above are what execution honors today.">
        <AiWorkspaceSpecSections spec={spec} update={updateSpec} sourceRegistry={sourceRegistry} />
      </GovernanceRecord>

      {/* AI scoring is driven by the rubric assigned at the resource level (Grading Rubric field).
          The legacy `evaluationCriteria` field in Content is still honored by the backend as a
          fallback for resources with no rubric, but is no longer editable from this form. */}
    </div>
  );

  const PORTLET_RENDERERS: Record<PortletId, () => JSX.Element> = {
    pathways: renderPathways,
    matrix: renderMatrix,
    guardrails: renderGuardrails,
    authority: renderAuthority,
    monitoring: renderMonitoring,
    sources: renderSources,
    formulary: renderFormulary,
    ai: renderAiWorkspace,
    testing: renderTesting,
    versioning: renderVersioning,
  };

  const active = STUDIO_PORTLETS.find((p) => p.id === activePortlet) ?? STUDIO_PORTLETS[0];

  // Slide 4's panel splits the ten items into two visual columns; the rail
  // mirrors that as two labeled groups. Numbering is the global slide order.
  const railGroups: Array<{ label: string; ids: PortletId[] }> = [
    { label: "Clinical specifications", ids: ["pathways", "matrix", "guardrails", "authority", "monitoring"] },
    { label: "Sources, AI & governance", ids: ["sources", "formulary", "ai", "testing", "versioning"] },
  ];

  // Masthead governance chips (the mock's app bar): condition, lineage, gate state.
  const gateItemsAll = spec.publicationGate?.items || [];
  const gateEff = (it: { id: string; status: string }) => computedGate[it.id]?.status ?? it.status;
  const gateApplicable = gateItemsAll.filter((i) => gateEff(i) !== "na");
  const gateMet = gateApplicable.filter((i) => gateEff(i) === "met").length;
  const conditionName = clinicalMasters?.conditions.find((c) => c.conditionKey === config.condition)?.name ?? config.condition;

  // §4 publish action — the gate (§13.5) must be fully met; the working config
  // (minus its own publication record) becomes the snapshot learners execute.
  const publishBlockers = gateApplicable.filter((i) => gateEff(i) !== "met").map((i) => i.requirement);
  const publishResource = () => {
    const { publication: _prev, ...working } = config;
    const version = (config.publication?.version ?? 0) + 1;
    if (!window.confirm(
      `Publish v${version}?\n\nLearners will execute this edition. Further edits keep learners on v${version} until you republish. Remember to Save afterwards.`,
    )) return;
    updateConfig({
      publication: { status: "published", version, publishedAt: new Date().toISOString(), snapshot: working },
    });
  };

  return (
    <div className="drs">
      <div className="drs-masthead">
        <span className="drs-masthead-title">Decision Resource Studio</span>
        <span className="drs-masthead-sub">Configure · Govern · Publish</span>
        {config.isBaseTemplate && (
          <span className="drs-mode-badge base" title="This is the governed master (layer 4). Edits here shape every FUTURE client copy — existing copies keep the version they were made from. Freely editable; the publication gate and version bumps are its guardrails.">
            📐 Reference template
          </span>
        )}
        {!config.isBaseTemplate && derived && (
          <span className="drs-mode-badge copy" title="This is one customer's deployment copy (layer 5). Class B reference content is read-only until overridden; Class A is locked; CCR values are resolved here. The reference template stays intact.">
            🏥 Client deployment copy
          </span>
        )}
        <span className="drs-masthead-chips">
          {conditionName && <span className="drs-chip">Condition: {conditionName}</span>}
          {config.isBaseTemplate && (
            <span className="drs-chip drs-chip-base">SynerGen reference v{config.templateVersion ?? 1}</span>
          )}
          {!config.isBaseTemplate && config.basedOn && (
            <span className="drs-chip">from {config.basedOn.templateKey ?? `#${config.basedOn.resourceId}`}{config.basedOn.version ? ` v${config.basedOn.version}` : ""}</span>
          )}
          {config.publication?.status === "published" && (
            <span className="drs-chip drs-chip-base">Published v{config.publication.version}</span>
          )}
          {config.publication?.status === "draft" && (
            <span className="drs-chip drs-chip-warn" title={`Modified since v${config.publication.version} — learners still execute v${config.publication.version}`}>
              Draft (v{config.publication.version} live)
            </span>
          )}
          {gateApplicable.length > 0 && (
            <button
              type="button"
              className={"drs-chip drs-chip-gate" + (gateMet === gateApplicable.length ? " met" : "")}
              title={publishBlockers.length > 0
                ? `Publication gate (§13.5) — outstanding:\n• ${publishBlockers.join("\n• ")}`
                : "Publication gate (§13.5) — all conditions met"}
              onClick={() => setActivePortlet("versioning")}
            >
              {gateMet === gateApplicable.length ? "Ready to publish ✓" : `Ready to publish ${gateMet}/${gateApplicable.length}`}
            </button>
          )}
        </span>
      </div>
      <div className="drs-layout">
        <div className="drs-rail" role="tablist" aria-orientation="vertical" aria-label="Build and govern the Decision Resource">
          {railGroups.map((g) => (
            <React.Fragment key={g.label}>
              <div className="drs-rail-group">{g.label}</div>
              {g.ids.map((id) => {
                const p = STUDIO_PORTLETS.find((x) => x.id === id)!;
                const num = STUDIO_PORTLETS.findIndex((x) => x.id === id) + 1;
                const badge = portletBadge(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="tab"
                    aria-selected={p.id === activePortlet}
                    className={"drs-rail-item" + (p.id === activePortlet ? " active" : "")}
                    title={p.hint}
                    onClick={() => setActivePortlet(p.id)}
                  >
                    <span className="drs-rail-num">{num}</span>
                    <span className="drs-rail-label">
                      {p.label}
                      <span className="drs-rail-hint">{p.hint}</span>
                    </span>
                    {badge !== null && <span className="drs-tab-badge">{badge}</span>}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div className="drs-main">
          <div className="drs-portlet-hint">{active.icon} {active.label} — {active.hint}</div>
          {(["pathways", "matrix", "guardrails", "authority"] as PortletId[]).includes(activePortlet) && (
            <div className="drs-class-legend" title="§6 — every governed rule carries one of these classes">
              <span className="drs-class-legend-item"><span className="cs-class cs-class-a">A</span> Mandatory safety — locked on client copies, may never be weakened</span>
              <span className="drs-class-legend-item"><span className="cs-class cs-class-b">B</span> SynerGen reference — clients may override via an explicit act; edits become C</span>
              <span className="drs-class-legend-item"><span className="cs-class cs-class-c">C</span> Deployment-specific — the client's own content, client-governed</span>
            </div>
          )}
          <div className="drs-portlet-body">{PORTLET_RENDERERS[activePortlet]()}</div>
        </div>
      </div>
    </div>
  );
};

export default DecisionResourceStudio;
